import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useUserSettings } from "../context/UserSettingsContext";
import { useLanguage } from "../context/LanguageContext";
import { COUNTRIES } from "../utils/locationUtils";

/**
 * Shown when neither GPS nor IP geolocation could determine the user's country.
 * Lets them pick their country manually so country-based content filtering works.
 */
export default function LocationPrompt() {
  const { locationPromptNeeded, saveCountry } = useUserSettings();
  const { t } = useLanguage();
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Don't render if not needed or already dismissed this session.
  if (!locationPromptNeeded || dismissed) return null;

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    await saveCountry(selected);
    setSaving(false);
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <LocationOnIcon color="primary" />
        {t("location.promptTitle")}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("location.promptDesc")}
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>{t("location.country")}</InputLabel>
          <Select
            value={selected}
            label={t("location.country")}
            onChange={(e) => setSelected(e.target.value)}
          >
            {COUNTRIES.map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          size="small"
          color="inherit"
          onClick={() => setDismissed(true)}
          sx={{ color: "text.secondary" }}
        >
          {t("location.promptSkip")}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={!selected || saving}
        >
          {saving ? t("common.saving") : t("location.promptAction")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
